import { motion } from "framer-motion";
import { Phone, MapPin, Instagram, MessageCircle, Navigation } from "lucide-react";
import {
  PHONE_DISPLAY,
  PHONE_TEL,
  ADDRESS,
  INSTAGRAM_HANDLE,
  INSTAGRAM_URL,
  SERVICE_AREA,
  AREA_CITIES,
  waLink,
  DEFAULT_WA_MESSAGE,
} from "../data/content";

export const Contact = () => {
  return (
    <section id="contactos" data-testid="contact-section" className="py-20 sm:py-28 bg-[#0A1128]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold tracking-[0.3em] uppercase text-sky-400">
              Fale connosco
            </span>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              Peça já o seu orçamento gratuito
            </h2>
            <p className="mt-4 text-base text-slate-400 leading-relaxed max-w-lg">
              Envie-nos uma mensagem no WhatsApp com uma fotografia do que quer
              limpar e receba o orçamento em minutos. Deslocamo-nos ao seu
              domicílio em todo o {SERVICE_AREA}.
            </p>

            <a
              href={waLink(DEFAULT_WA_MESSAGE)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-button"
              className="mt-8 inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1eb857] text-[#06281a] font-bold text-base px-8 py-4 rounded-full transition-all shadow-xl shadow-[#25D366]/30 hover:-translate-y-0.5"
            >
              <MessageCircle className="h-5 w-5" />
              Pedir Orçamento no WhatsApp
            </a>

            <div className="mt-10 space-y-5">
              <a
                href={PHONE_TEL}
                data-testid="contact-phone"
                className="flex items-center gap-4 group"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-400/15 text-sky-300 ring-1 ring-sky-400/30 group-hover:bg-sky-400/25 transition-colors">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <span className="block text-xs uppercase tracking-widest text-slate-500">
                    Telefone
                  </span>
                  <span className="font-display text-lg font-bold text-white group-hover:text-sky-300 transition-colors">
                    {PHONE_DISPLAY}
                  </span>
                </div>
              </a>

              <a
                href="https://www.google.com/maps/search/?api=1&query=Avenida+de+Fran%C3%A7a+4050-276+Porto"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-address"
                className="flex items-center gap-4 group"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-400/15 text-sky-300 ring-1 ring-sky-400/30 group-hover:bg-sky-400/25 transition-colors">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <span className="block text-xs uppercase tracking-widest text-slate-500">
                    Morada
                  </span>
                  <span className="font-display text-lg font-bold text-white group-hover:text-sky-300 transition-colors">
                    {ADDRESS}
                  </span>
                </div>
              </a>

              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                className="flex items-center gap-4 group"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-400/15 text-sky-300 ring-1 ring-sky-400/30 group-hover:bg-sky-400/25 transition-colors">
                  <Instagram className="h-5 w-5" />
                </span>
                <div>
                  <span className="block text-xs uppercase tracking-widest text-slate-500">
                    Instagram
                  </span>
                  <span className="font-display text-lg font-bold text-white group-hover:text-sky-300 transition-colors">
                    {INSTAGRAM_HANDLE}
                  </span>
                </div>
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
            data-testid="service-area-card"
            className="card-glow rounded-3xl border border-sky-400/20 bg-gradient-to-b from-[#162550] to-[#101D42] p-8 sm:p-10"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-[#0A1128]">
                <Navigation className="h-6 w-6" />
              </span>
              <h3 className="font-display text-xl font-bold text-white">
                Zona de atuação: {SERVICE_AREA}
              </h3>
            </div>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              Serviço ao domicílio com deslocação rápida em toda a área
              metropolitana do Porto.
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {AREA_CITIES.map((city) => (
                <span
                  key={city}
                  data-testid={`area-city-${city.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-full border border-sky-400/25 bg-sky-400/10 px-4 py-1.5 text-sm font-medium text-sky-200"
                >
                  {city}
                </span>
              ))}
            </div>
            <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <p className="font-display text-sm font-bold text-white">
                Orçamento sem compromisso
              </p>
              <p className="mt-1 text-sm text-slate-400">
                Envie fotos pelo WhatsApp e receba uma estimativa imediata.
                Equipamento profissional de extração incluído em todos os
                serviços.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
